import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { landingVariant, sessionUtm, trackLanding, VIEW_TARGETS } from '../lib/landing';

/**
 * Suivi des landings comparées (lib/landing.js) : arrivée, sections vues, durée, départ.
 * Les clics d'achat partent de CheckoutContext (startCheckout), checkout et achat du Worker.
 */

// Signes de vie pendant la visite (secondes) : de quoi classer < 10 s et calculer une durée médiane.
const PINGS = [5, 10, 20, 30, 45, 60, 90, 120, 180, 240, 300, 420, 600, 900, 1200, 1800];

export default function LandingTracker() {
  const location = useLocation();

  useEffect(() => {
    // Toujours, même hors landing : les UTMs de l'URL d'arrivée sont gardés pour le checkout.
    sessionUtm();
    const variant = landingVariant(location.pathname);
    if (!variant) return undefined;
    const started = Date.now();
    const timers = [];
    timers.push(window.setTimeout(() => {
      const headline = document.querySelector('[data-headline]')?.getAttribute('data-headline') || '';
      trackLanding('landing_view', { ...(headline ? { headline } : {}), lang: document.documentElement.lang || '' });
    }, 300));

    const sent = new Set();
    const watched = new WeakSet();
    const views = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const event = entry.target.getAttribute('data-lp-view');
        if (!entry.isIntersecting || !event || sent.has(event)) return;
        sent.add(event);
        views.unobserve(entry.target);
        trackLanding(event);
      });
    }, { rootMargin: '-40% 0px -40% 0px', threshold: 0 });
    const scan = () => {
      Object.entries(VIEW_TARGETS[variant] || {}).forEach(([event, selector]) => {
        const node = document.querySelector(selector);
        if (!node || watched.has(node)) return;
        watched.add(node);
        node.setAttribute('data-lp-view', event);
        views.observe(node);
      });
    };
    [0, 800, 2500].forEach((delay) => timers.push(window.setTimeout(scan, delay)));

    PINGS.forEach((seconds) => timers.push(window.setTimeout(() => {
      if (document.visibilityState === 'visible') trackLanding('ping');
    }, seconds * 1000)));

    let left = false;
    const leave = () => {
      if (left || Date.now() - started < 1000) return;
      left = true;
      trackLanding('leave', { beacon: true });
    };
    const onHidden = () => {
      if (document.visibilityState === 'hidden') leave();
      else left = false;
    };
    document.addEventListener('visibilitychange', onHidden);
    window.addEventListener('pagehide', leave);
    return () => {
      timers.forEach((id) => window.clearTimeout(id));
      views.disconnect();
      document.removeEventListener('visibilitychange', onHidden);
      window.removeEventListener('pagehide', leave);
    };
  }, [location.pathname]);

  return null;
}
